import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getPaymentList, IPayment } from '../../apis/paymentList';

const DEFAULT_ERROR_MESSAGE = 'Could not get payments list!';
const DEFAULT_PAGE_SIZE = 10;

export enum PaymentStatus {
  Succeeded = 'succeeded',
  Failed = 'failed',
  Pending = 'pending',
  Refunded = 'refunded',
  Canceled = 'canceled',
  Paid = 'paid',
  Unpaid = 'unpaid',
}

export enum PaymentListOrderBy {
  PatientName = 'patient_name',
  Amount = 'amount',
  Status = 'status',
  PaymentDate = 'payment_date',
  InvoiceNumber = 'invoice_number',
  PaymentMethod = 'payment_method',
}

export interface IGetPaymentListParams {
  search?: string;
  status?: PaymentStatus | string;
  orderBy?: PaymentListOrderBy;
  orderDirection?: 'asc' | 'desc';
  pageNumber?: number;
  perPage?: number;
  fromDate?: Date;
  toDate?: Date;
  patientId?: number;
}

interface IPaymentListResponse {
  isLoading: boolean;
  isError: boolean;
  message: string;
  payments: IPayment[];
  totalPages: number;
  totalRecords: number;
  success: boolean;
  all_payment_count: number;
  paid_payment_count: number;
  unpaid_payment_count: number;
}

interface IInfinitePaymentListResponse {
  isLoading: boolean;
  isError: boolean;
  message: string;
  payments: IPayment[];
  totalPages: number;
  totalRecords: number;
  success: boolean;
  all_payment_count: number;
  paid_payment_count: number;
  unpaid_payment_count: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const getQueryKey = (params: IGetPaymentListParams) => [
  'payment-list',
  params.search,
  params.status,
  params.orderBy,
  params.orderDirection,
  params.fromDate,
  params.toDate,
  params.patientId,
  params.perPage || DEFAULT_PAGE_SIZE,
];

const getInfiniteQueryKey = (
  params: Omit<IGetPaymentListParams, 'pageNumber'>
) => [
  'payment-list-infinite',
  params.search,
  params.status,
  params.orderBy,
  params.orderDirection,
  params.fromDate,
  params.toDate,
  params.patientId,
  params.perPage || DEFAULT_PAGE_SIZE,
];

export function usePaymentList(
  params: IGetPaymentListParams = {}
): IPaymentListResponse {
  const finalParams = {
    ...params,
    orderDirection: params.orderDirection || 'desc',
  };

  const { data, isError, isLoading, isRefetching } = useQuery({
    queryKey: getQueryKey(finalParams),
    queryFn: () => getPaymentList(finalParams),
    enabled: !!params.patientId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isLoading || isRefetching,
    isError,
    message: data?.message || DEFAULT_ERROR_MESSAGE,
    payments: data?.payments || [],
    totalPages: data?.totalPages || 0,
    totalRecords: data?.totalRecords || 0,
    success: data?.success || false,
    all_payment_count: data?.all_payment_count || 0,
    paid_payment_count: data?.paid_payment_count || 0,
    unpaid_payment_count: data?.unpaid_payment_count || 0,
  };
}

export function useInfinitePaymentList(
  params: Omit<IGetPaymentListParams, 'pageNumber'> = {}
): IInfinitePaymentListResponse {
  const finalParams = {
    ...params,
    orderDirection: params.orderDirection || 'desc',
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: getInfiniteQueryKey(finalParams),
    queryFn: ({ pageParam = 1 }) => {
      return getPaymentList({ ...finalParams, pageNumber: pageParam });
    },
    enabled: !!params.patientId,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      if (currentPage < lastPage.totalPages) {
        return currentPage + 1;
      }
      return undefined;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const flattenedPayments = data?.pages?.flatMap((page) => page.payments) || [];
  const firstPage = data?.pages?.[0];

  return {
    isLoading,
    isError,
    message: firstPage?.message || DEFAULT_ERROR_MESSAGE,
    payments: flattenedPayments,
    totalPages: firstPage?.totalPages || 0,
    totalRecords: firstPage?.totalRecords || 0,
    success: firstPage?.success || false,
    all_payment_count: firstPage?.all_payment_count || 0,
    paid_payment_count: firstPage?.paid_payment_count || 0,
    unpaid_payment_count: firstPage?.unpaid_payment_count || 0,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
